import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid,
  alpha
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  AccessTime as PendingIcon
} from '@mui/icons-material';
import { useStores } from 'stores';
import { Interview } from 'types/interview';
import { Icon } from '@iconify/react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import LoadingScreen from 'components/LoadingScreen';

const formatDate = (dateString?: string) => {
  if (!dateString) return null;
  try {
    return format(new Date(dateString), 'dd MMM yyyy', { locale: ru });
  } catch {
    return null;
  }
};

interface InterviewCardProps {
  interview: Interview;
  currentInterviewIds?: string[];
  onNavigate: (interview: Interview) => void;
}

const InterviewCard = ({ interview, currentInterviewIds = [], onNavigate }: InterviewCardProps) => {
  const index = interview.finish ? interview.count : interview.index
  const progress = interview.count > 0 ? ((index) / interview.count) * 100 : 0;
  const isCompleted = interview.finish;
  const canContinue = currentInterviewIds.includes(interview.interviewId as string)
  const isMissed = !isCompleted && !canContinue;
  const { interviewStore } = useStores();

  const handleClick = () => {
    if (!isCompleted && !canContinue) return;
    
    interviewStore.currentInterviewToResult = interview;
    onNavigate(interview);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const isClickable = isCompleted || canContinue;

  return (
    <Card
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : -1}
      role="button"
      aria-label={`Интервью на позицию ${interview.position}`}
      aria-disabled={!isClickable}
      variant="outlined"
      sx={{
        boxShadow: 'none',
        ...(isClickable
          ? {
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 12px 24px -4px ${alpha(theme.palette.primary.main, 0.16)}`,
                borderColor: 'primary.light'
              }
            }
          : {
            opacity: '0.8'
          })
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          {/* Верхняя часть: статус и прогресс */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Chip
              icon={
                isCompleted ? (
                  <CheckIcon />
                ) : isMissed ? (
                  <Icon icon={'tabler:calendar-off'} />
                ) : (
                  <PendingIcon />
                )
              }
              label={isCompleted ? 'Завершено' : isMissed ? 'Пропущено' : 'В процессе'}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: (theme) => {
                  if (isCompleted) return alpha(theme.palette.success.main, 0.12);
                  if (isMissed) return alpha(theme.palette.error.main, 1);
                  return alpha(theme.palette.warning.main, 0.12);
                },
                color: isCompleted ? 'success.dark' : isMissed ? 'common.white' : 'warning.dark',
                '& .MuiChip-icon': {
                  color: 'inherit'
                }
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                backgroundColor: 'grey.100',
                px: 1.5,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {index} / {interview.count} вопросов
            </Typography>
          </Box>

          {/* Название позиции */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.5,
                lineHeight: 1.3
              }}
            >
              {interview.position}
            </Typography>
            {interview.title && (
              <Stack direction="row">
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {interview.title}
                </Typography>
                {/* {(isCompleted || canContinue) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pt: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        transition: 'gap 0.2s ease',
                        '&:hover': { gap: 1 }
                      }}
                    >
                      {isCompleted ? 'Смотреть результаты' : 'Продолжить'}
                      <PlayIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Box>
                )} */}
              </Stack>
            )}
            {formatDate(interview.createdAt) && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                {formatDate(interview.createdAt)}
              </Typography>
            )}
          </Box>

          {/* Прогресс-бар */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.16),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 1,
                  background: (theme) =>
                    isCompleted
                      ? theme.palette.success.main
                      : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
                }
              }}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const InterviewsListPage = () => {
  const { personalDataStore } = useStores();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { interviewList } = personalDataStore;
  const personalId = personalDataStore.data.id;

  useEffect(() => {
    if (!personalId) {
      return;
    }

    const loadInterviews = async () => {
      setLoading(true);
      try {
        await personalDataStore.getInterviewList();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка при загрузке интервью');
      } finally {
        setLoading(false);
      }
    };
    loadInterviews();
  }, [personalDataStore, personalId]);

  const handleNavigateToInterview = (interview: Interview) => {
    // if (isCompleted) {
    //   navigate(`/results/${id}`);
    //   return
    // } else {
    //   navigate(`/video-interview/record/${id}`);
    // }
    navigate(`/results/${interview.id}`);
  };

  if (loading) {
    return (
      <LoadingScreen label="Получаем список интервью" />
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const interviewWithResults = interviewList.filter(i => i.finish)

  return (
    <>
      <Stack spacing={4} sx={{ mb: 4, height: '100%', overflow: 'auto' }}>
        {/* Заголовок */}
        <Box>
          <Typography variant="body1" color="text.secondary">
            Здесь вы можете просмотреть результаты всех пройденных интервью
          </Typography>
        </Box>

        {/* Список интервью */}
        {interviewWithResults.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'grey.300'
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Пока нет завершенных интервью
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Пройдите интервью, чтобы увидеть результаты здесь
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ ml: '-12px !important' }}>
            {interviewWithResults.map((interview) => (
              <Grid item xs={12} sm={6} md={4} key={interview.id}>
                <InterviewCard
                  interview={interview}
                  currentInterviewIds={personalDataStore?.interviewList.map(i => i.id) ?? undefined}
                  onNavigate={handleNavigateToInterview}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </>
  );
};

export default InterviewsListPage;
